/**
 * useEventProcessing Hook
 * Handles event normalization, sorting, and distribution across calendar days
 */
import { useMemo, useCallback } from 'react';
import { CalendarEvent, ProcessedEventMap, DayObject } from '../../../contracts/YearlyCalendarContracts';
import {
  processEventsForView,
  getEventsForDay,
  getEventCountForDay,
  hasMoreEvents,
  getVisibleEvents,
  isEventClickable,
  getEventColor,
  getEventTextColor,
} from '../utils/eventUtils';
import { getDateKey } from '../utils/dateUtils';

export interface UseEventProcessingOptions {
  events: CalendarEvent[];
  visibleDays: Date[];
  startingDay?: number;
  maxVisibleEvents?: number;
  expandedMode?: boolean;
}

export interface UseEventProcessingReturn {
  // Processed data
  eventMap: ProcessedEventMap;
  
  // Query functions
  getEvents: (date: Date) => CalendarEvent[];
  getEventCount: (date: Date) => number;
  hasMore: (date: Date) => boolean;
  getVisibleEventsForDay: (date: Date) => CalendarEvent[];
  getHiddenCount: (date: Date) => number;
  
  // Event helpers
  isClickable: (event: CalendarEvent) => boolean;
  getColor: (event: CalendarEvent) => string;
  getTextColor: (event: CalendarEvent) => string;
  
  // Day helpers
  getDayEventSummary: (day: DayObject) => {
    events: CalendarEvent[];
    visibleEvents: CalendarEvent[];
    hiddenCount: number;
    hasMore: boolean;
  };
}

/**
 * Custom hook for processing and managing calendar events
 */
export const useEventProcessing = (options: UseEventProcessingOptions): UseEventProcessingReturn => {
  const {
    events,
    visibleDays,
    startingDay = 0,
    maxVisibleEvents = 2,
    expandedMode = false,
  } = options;
  
  // Process events and create the event map
  const eventMap = useMemo(() => {
    if (!events || events.length === 0) {
      // Create empty map for visible days
      const emptyMap: ProcessedEventMap = {};
      visibleDays.forEach(day => {
        emptyMap[getDateKey(day)] = [];
      });
      return emptyMap;
    }
    
    return processEventsForView(events, visibleDays, startingDay, expandedMode);
  }, [events, visibleDays, startingDay, expandedMode]);
  
  // Get all events for a specific date
  const getEvents = useCallback((date: Date): CalendarEvent[] => {
    return getEventsForDay(eventMap, date);
  }, [eventMap]);
  
  // Get event count for a date
  const getEventCount = useCallback((date: Date): number => {
    return getEventCountForDay(eventMap, date);
  }, [eventMap]);
  
  // Check if date has more events than visible
  const hasMore = useCallback((date: Date): boolean => {
    return hasMoreEvents(eventMap, date, maxVisibleEvents);
  }, [eventMap, maxVisibleEvents]);
  
  // Get only visible events (respecting max limit)
  const getVisibleEventsForDay = useCallback((date: Date): CalendarEvent[] => {
    return getVisibleEvents(eventMap, date, maxVisibleEvents, expandedMode);
  }, [eventMap, maxVisibleEvents, expandedMode]);
  
  // Get count of hidden events
  const getHiddenCount = useCallback((date: Date): number => {
    const total = getEventCountForDay(eventMap, date);
    const visible = expandedMode ? total : Math.min(total, maxVisibleEvents);
    return Math.max(0, total - visible);
  }, [eventMap, maxVisibleEvents, expandedMode]);
  
  // Check if an event is clickable
  const isClickable = useCallback((event: CalendarEvent): boolean => {
    return isEventClickable(event);
  }, []);
  
  // Get event background color
  const getColor = useCallback((event: CalendarEvent): string => {
    return getEventColor(event);
  }, []);
  
  // Get event text color
  const getTextColor = useCallback((event: CalendarEvent): string => {
    return getEventTextColor(event);
  }, []);
  
  // Get complete event summary for a day
  const getDayEventSummary = useCallback((day: DayObject) => {
    const allEvents = getEvents(day.date);
    const visibleEvents = getVisibleEventsForDay(day.date);
    const hiddenCount = getHiddenCount(day.date);
    const hasMoreEvents = hiddenCount > 0;
    
    return {
      events: allEvents,
      visibleEvents,
      hiddenCount,
      hasMore: hasMoreEvents,
    };
  }, [getEvents, getVisibleEventsForDay, getHiddenCount]);
  
  return {
    eventMap,
    getEvents,
    getEventCount,
    hasMore,
    getVisibleEventsForDay,
    getHiddenCount,
    isClickable,
    getColor,
    getTextColor,
    getDayEventSummary,
  };
};

export default useEventProcessing;
